import * as React from 'react';
import {FormEvent, useCallback, useEffect, useMemo, useState} from 'react';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import {DialogContentText, DialogTitle} from '@mui/material';
import {css} from '@emotion/css';
import {EntityType, Entry, ReferenceEntry, RepositoryEntry} from '../types';
import {useAppSelector} from '../hooks';
import {recordDeleteLocalAction, selectAllEntries, selectEntryReadableValues} from '../containers/contentSlice';
import {useDispatch} from 'react-redux';
import {useEntryDeleteMutation} from '../services/records';
import { useDeleteNoteMutation, useLoadNoteQuery } from '../services/notes';
import { useRepositoryEntryDeleteMutation } from '../services/repositoryEntries';
import {selectIsAuthenticated} from '../containers/authSlice';
import {
	selectConfirmDeleteDialogEntry,
	selectIsConfirmDeleteDialogOpen,
	confirmDeleteDialogEntrySetState
} from '../containers/formDataSlice';
import {useNavigate} from 'react-router-dom';
import { entryGetReadableValue, recursiveChildRecordIdsFind } from '../common/util';
import { useLoadRepositoryEntry3Query } from '../services/repositoryEntries';

export type SubmitConfirmDeleteArgs = {
	redirectPath?: string;
	repositoryId?: string;
};

export const useConfirmDeleteEntryDialog = () => {
	const dispatch = useDispatch();
	const isConfirmDeleteDialogOpen = useAppSelector(selectIsConfirmDeleteDialogOpen);
	const confirmDeleteDialogEntry = useAppSelector(selectConfirmDeleteDialogEntry);
	const [entryDeleteMutation] = useEntryDeleteMutation();
	const [deleteNoteMutation] = useDeleteNoteMutation();
	const [deleteRepositoryEntryMutation] = useRepositoryEntryDeleteMutation();
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const navigate = useNavigate();

	const closeConfirmDeleteDialog = useCallback(() => {
		dispatch(confirmDeleteDialogEntrySetState(null));
	}, [dispatch]);

	const openConfirmDeleteDialog = useCallback((entry: Entry) => {
		dispatch(confirmDeleteDialogEntrySetState(entry));
	}, []);

	const submitConfirmDeleteDialog = useCallback(
		async ({ redirectPath, repositoryId }: SubmitConfirmDeleteArgs = {}) => {
			const entry = confirmDeleteDialogEntry as Entry;
			if (!entry) {
				return;
			}
			const { id } = entry;
			try {
				if (isAuthenticated) {
					if (entry.type === EntityType.Note) {
						await deleteNoteMutation({
							id,
							parentId: entry.parentId,
						}).unwrap();
					} else if (entry.type === EntityType.RepositoryEntry) {
						const rid =
							repositoryId ??
							(entry as RepositoryEntry).repositoryId;
						await deleteRepositoryEntryMutation({
							id,
							repositoryId: rid,
						}).unwrap();
					} else {
						await entryDeleteMutation({ id }).unwrap();
					}
				} else {
					dispatch(recordDeleteLocalAction(id));
				}
				if (redirectPath !== undefined) {
					navigate(redirectPath);
				}
				closeConfirmDeleteDialog();
			} catch {
				// Keep dialog open on failure so the user can retry or cancel.
			}
		},
		[
			confirmDeleteDialogEntry,
			isAuthenticated,
			entryDeleteMutation,
			deleteNoteMutation,
			deleteRepositoryEntryMutation,
			dispatch,
			navigate,
			closeConfirmDeleteDialog,
		],
	);

	return {isConfirmDeleteDialogOpen, confirmDeleteDialogEntry, openConfirmDeleteDialog, closeConfirmDeleteDialog, submitConfirmDeleteDialog};
};

interface Props {
	open: boolean;
	handleClose: () => void;
	handleSubmit: () => void;
	title: string;
	entry: Entry | null;
	dialogContent?: string;
}

function ConfirmDeleteEntryDialog({ open, handleClose, handleSubmit, title, entry, dialogContent}: Props) {
	const [cachedEntryLabel, setCachedEntryLabel] = useState('');
	const [dialogTitle, setDialogTitle] = useState<string | null>(null);
	const [acknowledgedSubEntriesDelete, setAcknowledgedSubEntriesDelete] = useState(false);
	const entryReadableValues = useAppSelector(selectEntryReadableValues);
	const allEntries = useAppSelector(selectAllEntries);
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const isNote = entry?.type === EntityType.Note;
	const { data: noteBundle, isFetching: isNoteBundleFetching } = useLoadNoteQuery(entry?.id ?? '', {
		skip: !open || !isAuthenticated || !isNote || !entry?.id,
	});
	const subEntryCount = useMemo(() => {
		if (!entry || !isNote) {
			return 0;
		}
		if (isAuthenticated) {
			if (noteBundle) {
				return noteBundle.subRecordCount;
			}
			return recursiveChildRecordIdsFind(entry.id, allEntries).length;
		}
		return recursiveChildRecordIdsFind(entry.id, allEntries).length;
	}, [allEntries, entry, isAuthenticated, isNote, noteBundle]);
	const hasSubEntries = subEntryCount > 0;
	const isSubEntryCountPending = isAuthenticated && isNote && isNoteBundleFetching && !noteBundle;
	const canDelete = !hasSubEntries || acknowledgedSubEntriesDelete;

	let repositoryEntryToFetchId;
	if (entry) {
		if (entry.type === EntityType.Reference) {
			repositoryEntryToFetchId = (entry as ReferenceEntry).repositoryEntryId;
		} else if (entry.type === EntityType.RepositoryEntry) {
			repositoryEntryToFetchId = entry.id;
		}
	}
	const { data: repositoryEntryBundle } = useLoadRepositoryEntry3Query(repositoryEntryToFetchId as string, {
		skip: !isAuthenticated || !repositoryEntryToFetchId || dialogContent !== undefined,
	});
	
	let entryLabel = '';
	if (entry && !isAuthenticated) {
		entryLabel = entryGetReadableValue(entry, entryReadableValues);
	} else if (entry) {
		switch (entry?.type) {
		case EntityType.Reference:
		case EntityType.RepositoryEntry:
			entryLabel = repositoryEntryBundle?.title ?? '';
			break;
		default:
			// Fallback to raw entry title property
			entryLabel = entryGetReadableValue(entry as Entry, new Map());
			break;
		}
	}

	// cache content to prevent it from disappearing during dialog fade out
	useEffect(() => {
		if (open) {
			setCachedEntryLabel(dialogContent ?? entryLabel);
			setDialogTitle(title);
		}
	}, [open, entryLabel, title, dialogContent]);

	useEffect(() => {
		if (!open) {
			setAcknowledgedSubEntriesDelete(false);
		}
	}, [open]);

	const onSubmit = (e: FormEvent) => {
		e.preventDefault();
		handleSubmit();
	};

	return (
		<Dialog open={open} onClose={handleClose} className={css`
			& .MuiPaper-root {
			  min-width: 320px;
			}
		`}>
			<DialogTitle>
				{dialogTitle}
			</DialogTitle>
			<form onSubmit={onSubmit} autoComplete='off'>
				<DialogContent>
					{cachedEntryLabel ? <DialogContentText sx={{textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden', mb: 1}}>
						&quot;{cachedEntryLabel}&quot;
					</DialogContentText> :
						<DialogContentText>
						This action can&apos;t be undone.
						</DialogContentText>}
					{hasSubEntries && (
						<FormControlLabel
							sx={{ mt: 2 }}
							control={
								<Checkbox
									checked={acknowledgedSubEntriesDelete}
									onChange={(_, checked) => setAcknowledgedSubEntriesDelete(checked)}
									color="error"
								/>
							}
							label={
								subEntryCount === 1
									? 'I understand that this record contains sub-entries that will also be permanently deleted.'
									: `I understand that this record contains ${subEntryCount} sub-entries that will also be permanently deleted.`
							}
						/>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose}>Cancel</Button>
					<Button
						type='submit'
						variant="contained"
						color='error'
						disabled={!canDelete || isSubEntryCountPending}
					>
						Delete
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
}

export default ConfirmDeleteEntryDialog;
